/**
 * Directory sync — queries + mutations.
 *
 * Backed by internal/tenant/directory.AdminHandler, mounted at
 * /api/v1/tenants/{tenant_id}/directory via WithPathTenant.
 *
 * Config CRUD is live; the actual LDAP/SCIM/Google sync workers
 * are a separate feature — TriggerSync enqueues a pending run and
 * returns immediately. The background worker that drains pending
 * runs lands alongside the directory-client implementations.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  scimOperationsSchema,
  scimPaginationSchema,
  scimSyncLogEntrySchema,
} from "./scim-operations.generated";
import { identitySourceListSchema, identitySourceSchema } from "./connections.generated";
import {
  directoryOverviewSchema,
  directorySyncTriggerResponseSchema,
  adInboundDirectorySchema,
  adInboundDirectoriesListResponseSchema,
  adOuListResponseSchema,
  adDirectoryUsersResponseSchema,
  adDirectoryGroupsResponseSchema,
  directorySyncRejectsResponseSchema,
  toAdInboundDirectoryPayload,
  type DirectoryConfig,
  type DirectoryOverview,
  type DirectorySyncTriggerResponse,
  type AdInboundDirectory,
  type AdInboundDirectoryWriteInput,
  type AdInboundDirectoriesListResponse,
  type AdDirectoryCredentials,
  type AdOuListResponse,
  type AdDirectoryUsersResponse,
  type AdDirectoryGroupsResponse,
  type AdDirectoryLinksFilter,
  type DirectorySyncRejectsResponse,
  type DirectorySyncRejectsFilter,
  workdayDirectorySchema,
  workdayProbeResultSchema,
  type WorkdayDirectory,
  type WorkdayProbeResult,
} from "./schemas";

// ── Inbound SCIM schemas ─────────────────────────────────────────────────
//
// The Directory feature module also surfaces INBOUND SCIM state
// (an external IdP pushing users to this tenant via the
// /scim/v2 endpoint) because that's a directory-sync surface
// from the operator's perspective even though the transport is
// inverted (push instead of pull). Server DTOs live in
// backend/pkg/api/tenant/scim.go.

export const scimTokenSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  description: z.string(),
  status: z.string(),
  expires_at: z.string().nullable().optional(),
  last_used_at: z.string().nullable().optional(),
  // The ACCOUNTABLE human — who answers for this now, as distinct from whoever
  // created it. Absent when nobody is, which the non-human identity census
  // renders as a finding rather than a blank.
  owner_admin_id: z.string().nullable().optional(),
  // The identity source this tenant-wide token is the provisioning half of
  //; absent when unlinked or organization-scoped.
  source_id: z.string().nullable().optional(),
  // The organization an organization-scoped token provisions into; absent on
  // a workforce tenant's token.
  organization_id: z.string().nullable().optional(),
  created_at: z.string(),
});
export type ScimToken = z.infer<typeof scimTokenSchema>;

export const scimTokenListSchema = z.object({
  data: z.array(scimTokenSchema),
  pagination: z.object({
    total: z.number(),
    offset: z.number(),
    limit: z.number(),
  }),
});
export type ScimTokenList = z.infer<typeof scimTokenListSchema>;

// Inbound SCIM activity. The report, its log rows and its pagination are
// generated from api/openapi/federation.yaml; the UI vocabularies below are
// derived from them. The server writes four operations (create, update,
// patch, delete) on two resource types, and health is one of three values.
export { scimOperationsSchema, scimPaginationSchema, scimSyncLogEntrySchema };
export const scimOperationSchema = scimSyncLogEntrySchema.shape.operation;
export type ScimOperation = z.infer<typeof scimOperationSchema>;
export const scimResourceTypeSchema = scimSyncLogEntrySchema.shape.resource_type;
export type ScimResourceType = z.infer<typeof scimResourceTypeSchema>;
export type ScimSyncLogEntry = z.infer<typeof scimSyncLogEntrySchema>;
export type ScimPagination = z.infer<typeof scimPaginationSchema>;
export type ScimOperations = z.infer<typeof scimOperationsSchema>;

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/directory`;
}

// ── Fetchers ──────────────────────────────────────────────────────────────

export async function fetchDirectoryOverview(
  tenantID: string,
): Promise<DirectoryOverview> {
  const raw = await apiFetch<unknown>(base(tenantID));
  return directoryOverviewSchema.parse(raw);
}

// ── Queries ───────────────────────────────────────────────────────────────

export function useDirectoryOverviewQuery(tenantID: string, enabled = true) {
  return useQuery<DirectoryOverview, ApiError>({
    queryKey: queryKeys.directory.overview(tenantID),
    queryFn: () => fetchDirectoryOverview(tenantID),
    enabled: enabled && tenantID.length > 0,
    refetchInterval: 30_000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────

export function useSaveDirectoryConfigMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<DirectoryOverview, ApiError, DirectoryConfig>({
    mutationFn: (config) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "PUT",
          body: config,
        }).then((raw) => directoryOverviewSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.directory.overview(tenantID), updated);
    },
  });
}

export function useDisableDirectoryMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<void>(base(tenantID), { method: "DELETE" }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.directory.all(tenantID) });
    },
  });
}

// ── Inbound SCIM queries ──────────────────────────────────────────────
//
// Backed by internal/tenant/scim.AdminHandler at
// /api/v1/tenants/{id}/scim/{tokens,operations}. Surfaced here
// (not in a separate scim feature) because the Directory page is
// the operator's one-stop "directory-sync status" surface; keeping
// inbound visible alongside outbound keeps the mental model of
// "what provisioning is happening for this tenant" on one page.

function scimBase(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/scim`;
}

export async function fetchScimTokens(
  tenantID: string,
): Promise<ScimTokenList> {
  const raw = await apiFetch<unknown>(`${scimBase(tenantID)}/tokens?status=active`);
  return scimTokenListSchema.parse(raw);
}

// Fetches the operations report. The default `limit=1` is used by
// the InboundScimCard which only needs the aggregate + the most
// recent log line; the dedicated /scim-operations page passes a
// larger limit to populate its table.
//
// We bound limit at 200 (the backend's `maxOpsLogLimit`) and
// reject negative offsets at the call site rather than trusting
// caller hygiene.
export async function fetchScimOperations(
  tenantID: string,
  options: { limit?: number; offset?: number } = {},
): Promise<ScimOperations> {
  const limit = Math.min(Math.max(options.limit ?? 1, 1), 200);
  const offset = Math.max(options.offset ?? 0, 0);
  const raw = await apiFetch<unknown>(
    `${scimBase(tenantID)}/operations?limit=${limit}&offset=${offset}`,
  );
  return scimOperationsSchema.parse(raw);
}

// fetchAllScimTokens — returns the unfiltered token list (active
// AND revoked) used by the management page. The card-side metric
// only counts active tokens and uses fetchScimTokens; the
// management page wants both states so the operator can audit
// which tokens were revoked and when.
//
// limit=200 matches the server's `maxTokenListLimit` so the
// management page renders the full list in a single round-trip.
// Real tenants have <10 tokens; the cap is a defensive ceiling
// against a degenerate (and operationally suspicious) state, not
// an expected pagination boundary.
export async function fetchAllScimTokens(
  tenantID: string,
): Promise<ScimTokenList> {
  const raw = await apiFetch<unknown>(`${scimBase(tenantID)}/tokens?limit=200`);
  return scimTokenListSchema.parse(raw);
}

export function useScimTokensQuery(tenantID: string, enabled = true) {
  return useQuery<ScimTokenList, ApiError>({
    queryKey: queryKeys.scimTokens.all(tenantID),
    queryFn: () => fetchScimTokens(tenantID),
    enabled: enabled && tenantID.length > 0,
    staleTime: 60_000,
  });
}

// useScimTokensManageQuery — list query for the dedicated SCIM
// tokens management page. Distinct cache entry from the active-only
// card query so the management page can show revoked tokens
// without re-fetching the card view.
export function useScimTokensManageQuery(tenantID: string) {
  return useQuery<ScimTokenList, ApiError>({
    queryKey: queryKeys.scimTokens.allTokens(tenantID),
    queryFn: () => fetchAllScimTokens(tenantID),
    enabled: tenantID.length > 0,
    refetchOnWindowFocus: false,
  });
}

// CreateScimTokenInput — request body for POST /scim/tokens.
// expires_at is an ISO-8601 string when present; the server
// rejects past timestamps. Description is required and capped
// at 256 chars on the server.
export interface CreateScimTokenInput {
  description: string;
  expires_at?: string | null;
  /**
   * The identity partition this token provisions INTO (CIAM Slice 9).
   * REQUIRED on customer tenants — each org brings its own upstream
   * IdP; the server refuses an org-less mint there. Omit on
   * workforce tenants (one upstream feeds the whole workforce).
   */
  organization_id?: string;
  /**
   * The identity source this token is the provisioning half of. Tenant-wide
   * tokens only; the source must be active and have no other active token.
   */
  source_id?: string;
}

// CreatedScimToken — the response shape from POST /scim/tokens.
// `token` is the plaintext bearer value, present ONLY in the
// create response. The page renders it once (reveal-then-mask)
// and never writes it to the cache.
export const createdScimTokenSchema = scimTokenSchema.extend({
  token: z.string(),
});
export type CreatedScimToken = z.infer<typeof createdScimTokenSchema>;

export function useCreateScimTokenMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<CreatedScimToken, ApiError, CreateScimTokenInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${scimBase(tenantID)}/tokens`, {
          method: "POST",
          body: input,
        }).then((raw) => createdScimTokenSchema.parse(raw)),
      ),
    // Invalidate token list caches so the new row appears, but we
    // never write the response (which carries the plaintext) into
    // the cache. The page reads `token` off the resolved promise
    // value; mutation-result is the only place it lives.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.scimTokens.all(tenantID) });
      qc.invalidateQueries({ queryKey: queryKeys.scimTokens.allTokens(tenantID) });
      // A token is the provisioning half of a source: the Sources rows follow it.
      qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) });
    },
  });
}

export function useRevokeScimTokenMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (tokenID) =>
      withStepUp(() =>
        apiFetch<void>(
          `${scimBase(tenantID)}/tokens/${encodeURIComponent(tokenID)}`,
          { method: "DELETE" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.scimTokens.all(tenantID) });
      qc.invalidateQueries({ queryKey: queryKeys.scimTokens.allTokens(tenantID) });
      // A token is the provisioning half of a source: the Sources rows follow it.
      qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) });
    },
  });
}

/**
 * Name (or clear) the accountable owner of a SCIM provisioning token.
 *
 * withStepUp because reassigning accountability is a governance act on a live
 * provisioning credential — the same gate the other mutations on this surface
 * carry, and the server may demand a fresh assertion for it.
 *
 * The invalidation is what keeps the NHI census honest: wireCensusInvalidation
 * watches queryKeys.scimTokens.all, so the roster's Owner column and its
 * "No owner recorded" finding refresh from this one call rather than going stale
 * behind the operator who just fixed them.
 */
export function useSetScimTokenOwnerMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { tokenID: string; ownerAdminID: string | null }>({
    mutationFn: ({ tokenID, ownerAdminID }) =>
      withStepUp(() =>
        apiFetch<void>(
          `${scimBase(tenantID)}/tokens/${encodeURIComponent(tokenID)}/owner`,
          {
            method: "PUT",
            // NOT pre-stringified: apiFetch serializes `body` itself, and its
            // contract says so ("Serialized with JSON.stringify — do not
            // pre-stringify"). Doing it here sent the JSON *string* as the body,
            // which no handler can unmarshal into a struct — a 400 on every save.
            //
            // An explicit null CLEARS the owner. It is sent rather than omitted
            // because the server distinguishes "no owner" from "field absent",
            // and unowned is a legitimate, chosen state.
            body: { owner_admin_id: ownerAdminID },
          },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.scimTokens.all(tenantID) });
      qc.invalidateQueries({ queryKey: queryKeys.scimTokens.allTokens(tenantID) });
      // A token is the provisioning half of a source: the Sources rows follow it.
      qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) });
    },
  });
}

export function useScimOperationsQuery(tenantID: string) {
  return useQuery<ScimOperations, ApiError>({
    queryKey: ["scim", "operations", tenantID],
    queryFn: () => fetchScimOperations(tenantID),
    enabled: tenantID.length > 0,
    // Same poll cadence as the directory overview — keeps "last
    // activity" fresh without a dedicated background worker.
    refetchInterval: 30_000,
  });
}

// useScimOperationsListQuery — paginated query used by the
// dedicated SCIM-operations page. Distinct from
// useScimOperationsQuery (above) so the card and the page each
// get their own cache entries with their own poll cadences;
// otherwise the card's `?limit=1` request would invalidate the
// page's `?limit=50` and cause flicker.
//
// `placeholderData: keepPreviousData` ensures the table doesn't
// flash empty between page-changes; `refetchOnWindowFocus` is
// off because operators investigating a SCIM incident scroll
// through history, not at-the-moment activity, and a focus
// refetch would jump them away from the row they were reading.
//
// Polling is enabled only when `live` is true — the page sets
// this when the operator is on page 1 with no filters
// (offset === 0 && !errors_only). When the operator paginates
// or filters, polling is disabled so the table doesn't shift
// under their cursor mid-investigation. SCIM is push-based —
// the upstream IdP POSTs to PureID's /scim/v2 endpoint — so
// "new operations" appear on the server as soon as the IdP
// pushes them; a 30s poll surfaces them in the live view at
// roughly the same cadence the operator would refresh manually.
export function useScimOperationsListQuery(
  tenantID: string,
  limit: number,
  offset: number,
  live: boolean,
) {
  return useQuery<ScimOperations, ApiError>({
    queryKey: ["scim", "operations-list", tenantID, limit, offset],
    queryFn: () => fetchScimOperations(tenantID, { limit, offset }),
    enabled: tenantID.length > 0,
    placeholderData: (previous) => previous,
    refetchOnWindowFocus: false,
    refetchInterval: live ? 30_000 : false,
  });
}

export function useTriggerDirectorySyncMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<DirectorySyncTriggerResponse, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/sync`, {
          method: "POST",
        }).then((raw) => directorySyncTriggerResponseSchema.parse(raw)),
      ),
    onSuccess: (response) => {
      // The usual answer is the refreshed overview, whose last_run is the
      // queued run; the bare status only arrives when that refresh failed.
      if ("status" in response) qc.setQueryData(queryKeys.directory.overview(tenantID), response);
      qc.invalidateQueries({ queryKey: queryKeys.directory.all(tenantID) });
    },
  });
}

// ── AD inbound directories (Renegadir-Connect) ──────────────────────────
//
// Backed by internal/tenant/directorysync.AdminHandler, mounted at
// /api/v1/tenants/{tenant_id}/ad-directories — a distinct feature from
// the outbound DirectoryConfig above (different auth model, different
// direction) and from inbound SCIM (generic IdP push vs. AD-specific
// OAuth2 client-credentials). See schemas.ts for the AD-inbound schema
// block; no expression-language / transform support anywhere here
// (scope cut).

function adDirectoryBase(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/ad-directories`;
}

function adDirectoryDetailPath(tenantID: string, directoryID: string): string {
  return `${adDirectoryBase(tenantID)}/${encodeURIComponent(directoryID)}`;
}

// ── Fetchers ──────────────────────────────────────────────────────────────

export async function fetchAdInboundDirectories(
  tenantID: string,
  offset = 0,
): Promise<AdInboundDirectoriesListResponse> {
  const raw = await apiFetch<unknown>(adDirectoryBase(tenantID), {
    searchParams: { offset, limit: 25 },
  });
  return adInboundDirectoriesListResponseSchema.parse(raw);
}

export async function fetchAdInboundDirectory(
  tenantID: string,
  directoryID: string,
): Promise<AdInboundDirectory> {
  const raw = await apiFetch<unknown>(adDirectoryDetailPath(tenantID, directoryID));
  return adInboundDirectorySchema.parse(raw);
}

export async function fetchAdDirectoryOuList(
  tenantID: string,
  directoryID: string,
): Promise<AdOuListResponse> {
  const raw = await apiFetch<unknown>(`${adDirectoryDetailPath(tenantID, directoryID)}/ou-list`);
  return adOuListResponseSchema.parse(raw);
}

export async function fetchAdDirectoryUsers(
  tenantID: string,
  directoryID: string,
  filter: AdDirectoryLinksFilter = {},
): Promise<AdDirectoryUsersResponse> {
  const raw = await apiFetch<unknown>(`${adDirectoryDetailPath(tenantID, directoryID)}/users`, {
    searchParams: {
      offset: filter.offset,
      sync_status: filter.sync_status,
      q: filter.q,
    },
  });
  return adDirectoryUsersResponseSchema.parse(raw);
}

export async function fetchAdDirectoryGroups(
  tenantID: string,
  directoryID: string,
  filter: AdDirectoryLinksFilter = {},
): Promise<AdDirectoryGroupsResponse> {
  const raw = await apiFetch<unknown>(`${adDirectoryDetailPath(tenantID, directoryID)}/groups`, {
    searchParams: {
      offset: filter.offset,
      sync_status: filter.sync_status,
      q: filter.q,
    },
  });
  return adDirectoryGroupsResponseSchema.parse(raw);
}

/** Records that failed BEFORE resolving to a user or group — the failures
 *  the link tables structurally cannot hold. */
export async function fetchDirectorySyncRejects(
  tenantID: string,
  directoryID: string,
  filter: DirectorySyncRejectsFilter = {},
): Promise<DirectorySyncRejectsResponse> {
  const raw = await apiFetch<unknown>(`${adDirectoryDetailPath(tenantID, directoryID)}/rejects`, {
    searchParams: {
      offset: filter.offset,
      object_type: filter.object_type,
      reason: filter.reason,
      q: filter.q,
    },
  });
  return directorySyncRejectsResponseSchema.parse(raw);
}

// ── Queries ───────────────────────────────────────────────────────────────

export function useAdInboundDirectoriesQuery(tenantID: string, offset = 0) {
  return useQuery<AdInboundDirectoriesListResponse, ApiError>({
    queryKey: queryKeys.directory.adInbound.list(tenantID, offset),
    queryFn: () => fetchAdInboundDirectories(tenantID, offset),
    enabled: tenantID.length > 0,
    refetchInterval: 30_000,
  });
}

export function useAdInboundDirectoryQuery(tenantID: string, directoryID: string) {
  return useQuery<AdInboundDirectory, ApiError>({
    queryKey: queryKeys.directory.adInbound.detail(tenantID, directoryID),
    queryFn: () => fetchAdInboundDirectory(tenantID, directoryID),
    enabled: tenantID.length > 0 && directoryID.length > 0,
    refetchInterval: 30_000,
  });
}

export function useAdDirectoryOuListQuery(
  tenantID: string,
  directoryID: string,
  enabled: boolean,
) {
  return useQuery<AdOuListResponse, ApiError>({
    queryKey: queryKeys.directory.adInbound.ouList(tenantID, directoryID),
    queryFn: () => fetchAdDirectoryOuList(tenantID, directoryID),
    enabled: enabled && tenantID.length > 0 && directoryID.length > 0,
  });
}

export function useAdDirectoryUsersQuery(
  tenantID: string,
  directoryID: string,
  filter: AdDirectoryLinksFilter,
) {
  return useQuery<AdDirectoryUsersResponse, ApiError>({
    queryKey: queryKeys.directory.adInbound.users(tenantID, directoryID, filter),
    queryFn: () => fetchAdDirectoryUsers(tenantID, directoryID, filter),
    enabled: tenantID.length > 0 && directoryID.length > 0,
    placeholderData: (previous) => previous,
    refetchInterval: 30_000,
  });
}

export function useDirectorySyncRejectsQuery(
  tenantID: string,
  directoryID: string,
  filter: DirectorySyncRejectsFilter,
) {
  return useQuery<DirectorySyncRejectsResponse, ApiError>({
    queryKey: queryKeys.directory.adInbound.rejects(tenantID, directoryID, filter),
    queryFn: () => fetchDirectorySyncRejects(tenantID, directoryID, filter),
    enabled: tenantID.length > 0 && directoryID.length > 0,
    placeholderData: (previous) => previous,
    // 30s, matching the directory-sync status polling cadence: a reject
    // clears itself the moment the underlying record syncs, so a stale list
    // shows problems that are already fixed.
    refetchInterval: 30_000,
  });
}

export function useAdDirectoryGroupsQuery(
  tenantID: string,
  directoryID: string,
  filter: AdDirectoryLinksFilter,
) {
  return useQuery<AdDirectoryGroupsResponse, ApiError>({
    queryKey: queryKeys.directory.adInbound.groups(tenantID, directoryID, filter),
    queryFn: () => fetchAdDirectoryGroups(tenantID, directoryID, filter),
    enabled: tenantID.length > 0 && directoryID.length > 0,
    placeholderData: (previous) => previous,
    refetchInterval: 30_000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────

export function useCreateAdInboundDirectoryMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<AdInboundDirectory, ApiError, AdInboundDirectoryWriteInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(adDirectoryBase(tenantID), {
          method: "POST",
          body: toAdInboundDirectoryPayload(input),
        }).then((raw) => adInboundDirectorySchema.parse(raw)),
      ),
    // The create response carries the OAuth client_secret in plaintext,
    // exactly once. We invalidate the list (so the new row appears) but
    // deliberately never setQueryData/cache the response itself — the
    // secret lives only in the resolved promise value the caller reveals
    // once (see oauth-credentials-panel.tsx).
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.directory.adInbound.all(tenantID) });
    },
  });
}

export function useUpdateAdInboundDirectoryMutation(tenantID: string, directoryID: string) {
  const qc = useQueryClient();
  return useMutation<AdInboundDirectory, ApiError, AdInboundDirectoryWriteInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        // PATCH, not PUT — the backend registers this route as
        // `r.Patch("/ad-directories/{directory_id}", h.Update)` and its
        // payload is a partial (`updateDirectoryRequest`, all-pointer).
        apiFetch<unknown>(adDirectoryDetailPath(tenantID, directoryID), {
          method: "PATCH",
          body: toAdInboundDirectoryPayload(input),
        }).then((raw) => adInboundDirectorySchema.parse(raw)),
      ),
    // Update responses never carry a secret (client_secret is always
    // null here), so caching the full record is safe.
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.directory.adInbound.detail(tenantID, directoryID), updated);
      qc.invalidateQueries({ queryKey: queryKeys.directory.adInbound.all(tenantID) });
    },
  });
}

/**
 * Name (or clear) the accountable owner of an AD-inbound directory.
 *
 * This is the highest-blast-radius non-human on the census: its on-prem agent
 * holds a standing OAuth client secret with NO expiry (rotation only), provisions
 * users, and names itself as the audit actor. Before migration 000016 it could
 * not name an accountable human at all.
 *
 * PUT, unlike the PATCH above: this replaces one field wholesale and an explicit
 * null is a meaningful value (clear the owner), which a partial-update payload
 * cannot express.
 */
export function useSetAdInboundDirectoryOwnerMutation(tenantID: string, directoryID: string) {
  const qc = useQueryClient();
  return useMutation<AdInboundDirectory, ApiError, string | null>({
    mutationFn: (ownerAdminID) =>
      withStepUp(() =>
        apiFetch<unknown>(`${adDirectoryDetailPath(tenantID, directoryID)}/owner`, {
          method: "PUT",
          // Not pre-stringified — apiFetch serializes it. See the SCIM hook above.
          body: { owner_admin_id: ownerAdminID },
        }).then((raw) => adInboundDirectorySchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.directory.adInbound.detail(tenantID, directoryID), updated);
      // Sweeps the list AND the census: wireCensusInvalidation watches
      // directory.adInbound.all, so the roster's Owner column refreshes from
      // this one call.
      qc.invalidateQueries({ queryKey: queryKeys.directory.adInbound.all(tenantID) });
    },
  });
}

export function useDeleteAdInboundDirectoryMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (directoryID) =>
      withStepUp(() =>
        apiFetch<void>(adDirectoryDetailPath(tenantID, directoryID), { method: "DELETE" }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.directory.adInbound.all(tenantID) });
    },
  });
}

// Rotate-on-reveal: the OAuth client secret is never stored recoverable
// server-side, so "reveal" actually mints a fresh secret and shows it
// once. Gated behind directory.manage_secrets + step-up (see
// role-permissions.ts / oauth-credentials-panel.tsx). Never cached — the
// plaintext lives only in the resolved mutation value.
export function useRotateAdDirectoryCredentialsMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<AdDirectoryCredentials, ApiError, string>({
    mutationFn: (directoryID) =>
      withStepUp(() =>
        apiFetch<unknown>(`${adDirectoryDetailPath(tenantID, directoryID)}/rotate-credentials`, {
          method: "POST",
        }).then((raw) => {
          // The endpoint answers with the whole directory record; the
          // freshly-minted secret rides on oauth_application. Narrow it
          // here so nothing downstream can hand the full (cacheable)
          // object to setQueryData while it still carries plaintext.
          const dir = adInboundDirectorySchema.parse(raw);
          const secret = dir.oauth_application.client_secret;
          if (!secret) {
            throw new Error("Rotate returned no client secret.");
          }
          return { client_id: dir.oauth_application.client_id, client_secret: secret };
        }),
      ),
    onSuccess: (_result, directoryID) => {
      qc.invalidateQueries({ queryKey: queryKeys.directory.adInbound.detail(tenantID, directoryID) });
    },
  });
}

// Manual attach — links an existing PureID user/group to the directory's
// roster outside of an AD sync cycle (e.g. binding a pre-existing local
// account before the first sync claims its external_id).
export function useAttachAdDirectoryUserMutation(tenantID: string, directoryID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { user_id: string }>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<void>(`${adDirectoryDetailPath(tenantID, directoryID)}/users`, {
          method: "POST",
          body: input,
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.directory.adInbound.detail(tenantID, directoryID) });
    },
  });
}

export function useAttachAdDirectoryGroupMutation(tenantID: string, directoryID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { group_id: string }>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<void>(`${adDirectoryDetailPath(tenantID, directoryID)}/groups`, {
          method: "POST",
          body: input,
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.directory.adInbound.detail(tenantID, directoryID) });
    },
  });
}

// ── Identity sources ────────────────────────────────────────
//
// The administrator's object for an upstream system: one row per Okta,
// Entra, Authentik or other enterprise app, carrying its two halves by id.
// Two rules follow it: federation auto-link by email stays within the source
// that brought a person, and an upstream-owned source refuses console edits
// of its people's profile attributes.

export { identitySourceSchema };
export const identitySourceKindSchema = identitySourceSchema.shape.kind;
export type IdentitySourceKind = z.infer<typeof identitySourceKindSchema>;
export const recordOwnershipSchema = identitySourceSchema.shape.record_ownership;
export type RecordOwnership = z.infer<typeof recordOwnershipSchema>;
export const identitySourceStatusSchema = identitySourceSchema.shape.status;
export type IdentitySource = z.infer<typeof identitySourceSchema>;

function sourcesBase(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/identity-sources`;
}

export async function fetchIdentitySources(tenantID: string): Promise<IdentitySource[]> {
  const raw = await apiFetch<unknown>(sourcesBase(tenantID));
  return identitySourceListSchema.parse(raw).data;
}

export function useIdentitySourcesQuery(tenantID: string, enabled = true) {
  return useQuery<IdentitySource[], ApiError>({
    queryKey: queryKeys.identitySources.all(tenantID),
    queryFn: () => fetchIdentitySources(tenantID),
    enabled: enabled && tenantID.length > 0,
    staleTime: 60_000,
  });
}

export interface IdentitySourceInput {
  name: string;
  kind: IdentitySourceKind;
  record_ownership: RecordOwnership;
}

export interface IdentitySourcePatch {
  name?: string;
  kind?: IdentitySourceKind;
  record_ownership?: RecordOwnership;
  status?: z.infer<typeof identitySourceStatusSchema>;
}

// Every source write is a step-up on the server (directory.manage plus a
// fresh assertion); withStepUp runs the ceremony when it is demanded.
export function useCreateIdentitySourceMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<IdentitySource, ApiError, IdentitySourceInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(sourcesBase(tenantID), { method: "POST", body: input }).then((raw) => identitySourceSchema.parse(raw)),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) }),
  });
}

export function useUpdateIdentitySourceMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<IdentitySource, ApiError, { sourceID: string; patch: IdentitySourcePatch }>({
    mutationFn: ({ sourceID, patch }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${sourcesBase(tenantID)}/${encodeURIComponent(sourceID)}`, { method: "PATCH", body: patch }).then((raw) => identitySourceSchema.parse(raw)),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) }),
  });
}

export function useDeleteIdentitySourceMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (sourceID) =>
      withStepUp(() => apiFetch<void>(`${sourcesBase(tenantID)}/${encodeURIComponent(sourceID)}`, { method: "DELETE" })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) }),
  });
}

// Link a tenant-wide token to a source, or unlink it (null). Step-up on the
// server; the token lists and the sources both change.
export function useSetScimTokenSourceMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<ScimToken, ApiError, { tokenID: string; sourceID: string | null }>({
    mutationFn: ({ tokenID, sourceID }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${scimBase(tenantID)}/tokens/${encodeURIComponent(tokenID)}/source`, {
          method: "PUT",
          body: { source_id: sourceID },
        }).then((raw) => scimTokenSchema.parse(raw)),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.scimTokens.all(tenantID) });
      void qc.invalidateQueries({ queryKey: queryKeys.scimTokens.allTokens(tenantID) });
      void qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) });
    },
  });
}

// ── Workday directories ─────────────────────────────────────────────────
//
// Backed by directorysync.WorkdayAdminHandler at
// /api/v1/tenants/{tenant_id}/workday-directories. Adding, changing and
// enabling one, and entering or withdrawing its credential, prompt for a
// step-up; reading its fields and pulling now do not. The password lives only
// in the credential mutation's arguments and is never cached.

function workdayBase(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/workday-directories`;
}

function workdayPath(tenantID: string, directoryID: string): string {
  return `${workdayBase(tenantID)}/${encodeURIComponent(directoryID)}`;
}

export async function fetchWorkdayDirectory(tenantID: string, directoryID: string): Promise<WorkdayDirectory> {
  const raw = await apiFetch<unknown>(workdayPath(tenantID, directoryID));
  return workdayDirectorySchema.parse(raw);
}

export function useWorkdayDirectoryQuery(tenantID: string, directoryID: string, enabled = true) {
  return useQuery<WorkdayDirectory, ApiError>({
    queryKey: queryKeys.directory.adInbound.workday(tenantID, directoryID),
    queryFn: () => fetchWorkdayDirectory(tenantID, directoryID),
    enabled: enabled && tenantID.length > 0 && directoryID.length > 0,
    refetchInterval: 30_000,
  });
}

function useWorkdayMutation<V>(
  tenantID: string,
  directoryID: string,
  send: (vars: V) => Promise<unknown>,
  // 0 for a mutation whose variables hold a secret: TanStack keeps a settled
  // mutation's variables in its cache until this lapses.
  gcTime?: number,
) {
  const qc = useQueryClient();
  return useMutation<WorkdayDirectory, ApiError, V>({
    gcTime,
    mutationFn: async (vars) => workdayDirectorySchema.parse(await send(vars)),
    onSuccess: (d) => {
      qc.setQueryData(queryKeys.directory.adInbound.workday(tenantID, directoryID), d);
      void qc.invalidateQueries({ queryKey: queryKeys.directory.adInbound.all(tenantID) });
    },
  });
}

export interface WorkdayCreateInput {
  name: string;
  report_url: string;
  identity_source_id: string;
  sync_interval_seconds: number;
}

export function useCreateWorkdayDirectoryMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<WorkdayDirectory, ApiError, WorkdayCreateInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(workdayBase(tenantID), { method: "POST", body: input }).then((raw) =>
          workdayDirectorySchema.parse(raw),
        ),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.directory.adInbound.all(tenantID) }),
  });
}

export interface WorkdayChangeInput {
  name?: string;
  report_url?: string;
  identity_source_id?: string;
  sync_interval_seconds?: number;
  attribute_mapping?: Record<string, string>;
  worker_id_field?: string;
  status_field?: string;
  active_values?: string[];
  inactive_values?: string[];
  enabled?: boolean;
}

export function useUpdateWorkdayDirectoryMutation(tenantID: string, directoryID: string) {
  return useWorkdayMutation(tenantID, directoryID, (change: WorkdayChangeInput) =>
    withStepUp(() => apiFetch<unknown>(workdayPath(tenantID, directoryID), { method: "PATCH", body: change })),
  );
}

/** The password lives only in this call's arguments. */
export function useSetWorkdayCredentialMutation(tenantID: string, directoryID: string) {
  return useWorkdayMutation(tenantID, directoryID, (vars: { username: string; password: string }) =>
    withStepUp(() =>
      apiFetch<unknown>(`${workdayPath(tenantID, directoryID)}/credential`, { method: "PUT", body: vars }),
    ),
    0,
  );
}

export function useWithdrawWorkdayCredentialMutation(tenantID: string, directoryID: string) {
  return useWorkdayMutation(tenantID, directoryID, () =>
    withStepUp(() => apiFetch<unknown>(`${workdayPath(tenantID, directoryID)}/credential`, { method: "DELETE" })),
  );
}

export function useProbeWorkdayMutation(tenantID: string, directoryID: string) {
  return useMutation<WorkdayProbeResult, ApiError, void>({
    mutationFn: async () =>
      workdayProbeResultSchema.parse(
        await apiFetch<unknown>(`${workdayPath(tenantID, directoryID)}/probe`, { method: "POST" }),
      ),
  });
}

/** Starts a read in the background; the answer is the directory while it runs. */
export function usePullWorkdayMutation(tenantID: string, directoryID: string) {
  return useWorkdayMutation(tenantID, directoryID, () =>
    apiFetch<unknown>(`${workdayPath(tenantID, directoryID)}/pull`, { method: "POST" }),
  );
}

/** Reads again, letting the read deprovision the people the last read stopped
 *  for; deprovisions restates how many. A step-up on the server. */
export function useConfirmWorkdayDeprovisionsMutation(tenantID: string, directoryID: string) {
  return useWorkdayMutation(tenantID, directoryID, (deprovisions: number) =>
    withStepUp(() =>
      apiFetch<unknown>(`${workdayPath(tenantID, directoryID)}/pull/confirm`, { method: "POST", body: { deprovisions } }),
    ),
  );
}

// ── Binding a feed to its identity source ───────────────────────────────
//
// Which claims a feed's writes are held to. A step-up on the server.

export function useSetAdDirectorySourceMutation(tenantID: string, directoryID: string) {
  const qc = useQueryClient();
  return useMutation<AdInboundDirectory, ApiError, string | null>({
    mutationFn: (sourceID) =>
      withStepUp(() =>
        apiFetch<unknown>(`${adDirectoryDetailPath(tenantID, directoryID)}/identity-source`, {
          method: "PUT",
          body: { source_id: sourceID },
        }).then((raw) => adInboundDirectorySchema.parse(raw)),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.directory.adInbound.all(tenantID) });
      void qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) });
    },
  });
}

export function useSetPullConnectionSourceMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<DirectoryOverview, ApiError, string | null>({
    mutationFn: (sourceID) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/identity-source`, { method: "PUT", body: { source_id: sourceID } }).then(
          (raw) => directoryOverviewSchema.parse(raw),
        ),
      ),
    onSuccess: (ov) => {
      qc.setQueryData(queryKeys.directory.overview(tenantID), ov);
      void qc.invalidateQueries({ queryKey: queryKeys.identitySources.all(tenantID) });
    },
  });
}
